import React, { useState, useEffect } from 'react';
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import { X, AlertTriangle, DollarSign, Clock, Phone, MessageCircle, Navigation } from "lucide-react";
import { toast } from "sonner";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Cancel Ride Dialog with Reasons
export const CancelRideDialog = ({ 
  isOpen, 
  onClose, 
  rideId, 
  userType, // "driver" or "rider"
  token,
  onCancelled,
  waitMinutes = 0 // For showing wait fee eligibility
}) => {
  const [reasons, setReasons] = useState([]);
  const [selectedReason, setSelectedReason] = useState(null);
  const [otherText, setOtherText] = useState('');
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (isOpen) {
      fetchReasons();
    }
  }, [isOpen, userType]);

  const fetchReasons = async () => {
    try {
      const res = await fetch(`${API}/safety/cancel-reasons/${userType}`);
      const data = await res.json();
      setReasons(data.reasons || []);
    } catch (error) {
      console.error('Error fetching reasons:', error);
    }
  };

  const handleCancel = async () => {
    if (!selectedReason) {
      toast.error('Please select a reason');
      return;
    }

    setLoading(true);
    try {
      const res = await fetch(`${API}/rides/${rideId}/cancel-with-reason`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`
        },
        body: JSON.stringify({
          reason_id: selectedReason.id,
          reason_text: selectedReason.id === 'other' ? otherText : null
        })
      });

      const data = await res.json();
      if (data.success) {
        toast.success(data.fee_message);
        onCancelled?.(data);
        onClose();
      } else {
        toast.error(data.detail || 'Failed to cancel ride');
      }
    } catch (error) {
      toast.error('Error cancelling ride');
    } finally {
      setLoading(false);
    }
  };

  const showFeeWarning = userType === 'rider' || (userType === 'driver' && waitMinutes >= 4);

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 text-red-600">
            <X className="w-5 h-5" /> Cancel Ride
          </DialogTitle>
        </DialogHeader>

        <div className="py-2">
          {/* Fee Warning */}
          {showFeeWarning && (
            <div className="mb-4 p-3 bg-amber-50 rounded-xl border border-amber-200">
              <div className="flex items-center gap-2 text-amber-700">
                <DollarSign className="w-4 h-4" />
                <span className="font-medium">
                  {userType === 'driver' && waitMinutes >= 4 
                    ? '$5.00 wait fee will be charged to rider'
                    : '$5.00 cancellation fee may apply'}
                </span>
              </div>
            </div>
          )}

          {/* Wait Time Info for Drivers */}
          {userType === 'driver' && waitMinutes > 0 && (
            <div className="mb-4 p-3 bg-slate-50 rounded-xl">
              <div className="flex items-center gap-2 text-slate-600">
                <Clock className="w-4 h-4" />
                <span>Wait time: {waitMinutes.toFixed(0)} minutes</span>
                {waitMinutes >= 4 && (
                  <Badge className="bg-green-100 text-green-700">Fee Eligible</Badge>
                )}
              </div>
            </div>
          )}

          {/* Reason Selection */}
          <p className="text-sm text-slate-600 mb-3">Please select a reason:</p>
          <div className="space-y-2 max-h-60 overflow-y-auto">
            {reasons.map((reason) => (
              <button
                key={reason.id}
                onClick={() => setSelectedReason(reason)}
                className={`w-full p-3 text-left rounded-xl border transition-all ${
                  selectedReason?.id === reason.id
                    ? 'border-[#1E4DB7] bg-[#1E4DB7]/5'
                    : 'border-slate-200 hover:border-slate-300'
                }`}
              >
                <span className={selectedReason?.id === reason.id ? 'text-[#1E4DB7] font-medium' : ''}>
                  {reason.text}
                </span>
              </button>
            ))}
          </div>

          {/* Other Reason Text */}
          {selectedReason?.id === 'other' && (
            <div className="mt-3">
              <Textarea
                value={otherText}
                onChange={(e) => setOtherText(e.target.value)}
                placeholder="Please describe the reason..."
                className="resize-none"
                rows={3}
              />
            </div>
          )}
        </div>

        <DialogFooter className="flex-col gap-2 sm:flex-col">
          <Button
            onClick={handleCancel}
            disabled={!selectedReason || loading}
            className="w-full bg-red-600 hover:bg-red-700"
          >
            {loading ? 'Cancelling...' : 'Confirm Cancellation'}
          </Button>
          <Button variant="outline" onClick={onClose} className="w-full">
            Keep Ride
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

// Driver Quick Actions Bar
export const DriverQuickActions = ({ ride, token, onAction }) => {
  const [prompts, setPrompts] = useState([]);
  const [showCancel, setShowCancel] = useState(false);
  const [waitMinutes, setWaitMinutes] = useState(0);

  useEffect(() => {
    fetchPrompts();
    if (ride?.driver_arrived_at) {
      const interval = setInterval(calculateWaitTime, 1000);
      return () => clearInterval(interval);
    }
  }, [ride]);

  const fetchPrompts = async () => {
    try {
      const res = await fetch(`${API}/rides/quick-prompts`);
      const data = await res.json();
      setPrompts(data.prompts || []);
    } catch (error) {
      console.error('Error fetching prompts:', error);
    }
  };

  const calculateWaitTime = () => {
    if (ride?.driver_arrived_at) {
      const arrived = new Date(ride.driver_arrived_at);
      const now = new Date();
      const minutes = (now - arrived) / 1000 / 60;
      setWaitMinutes(minutes);
    }
  };

  const sendPrompt = async (promptId) => {
    try {
      const res = await fetch(`${API}/rides/${ride.id}/quick-prompt`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`
        },
        body: JSON.stringify({ ride_id: ride.id, prompt_id: promptId })
      });
      const data = await res.json();
      if (data.success) {
        toast.success(`Sent: "${data.message}"`);
        onAction?.('prompt_sent', data);
      }
    } catch (error) {
      toast.error('Failed to send message');
    }
  };

  const callRider = () => {
    const phone = ride.other_rider_phone || ride.rider_phone;
    if (phone) {
      window.open(`tel:${phone}`);
    }
  };

  const messageRider = () => {
    onAction?.('open_chat');
  };

  const getPromptIcon = (promptId) => {
    switch (promptId) {
      case 'arrived': return '📍';
      case 'on_way': return '🚗';
      case 'traffic': return '🚦';
      case 'gas': return '⛽';
      default: return '💬';
    }
  };

  return (
    <Card className="border-0 shadow-md">
      <CardContent className="p-4 space-y-4">
        {/* Quick Prompts */}
        <div>
          <p className="text-sm text-slate-500 mb-2">Quick Messages</p>
          <div className="grid grid-cols-2 gap-2">
            {prompts.map((prompt) => (
              <Button
                key={prompt.id}
                variant="outline"
                size="sm"
                onClick={() => sendPrompt(prompt.id)}
                className="justify-start text-left h-auto py-2"
              >
                <span className="mr-2">{getPromptIcon(prompt.id)}</span>
                {prompt.text}
              </Button>
            ))}
          </div>
        </div>

        {/* Contact Rider */}
        <div className="flex gap-2">
          <Button
            variant="outline"
            className="flex-1"
            onClick={callRider}
          >
            <Phone className="w-4 h-4 mr-2" /> Call Rider
          </Button>
          <Button
            variant="outline"
            className="flex-1"
            onClick={messageRider}
          >
            <MessageCircle className="w-4 h-4 mr-2" /> Message
          </Button>
        </div>

        {/* Wait Time Display */}
        {ride?.status === 'arrived' && (
          <div className={`p-3 rounded-xl ${waitMinutes >= 4 ? 'bg-amber-50 border border-amber-200' : 'bg-slate-50'}`}>
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2">
                <Clock className={`w-4 h-4 ${waitMinutes >= 4 ? 'text-amber-600' : 'text-slate-500'}`} />
                <span className={waitMinutes >= 4 ? 'text-amber-700 font-medium' : 'text-slate-600'}>
                  Waiting: {Math.floor(waitMinutes)}:{String(Math.floor((waitMinutes % 1) * 60)).padStart(2, '0')}
                </span>
              </div>
              {waitMinutes >= 4 && (
                <Badge className="bg-amber-500 text-white">$5 Fee Eligible</Badge>
              )}
            </div>
            {waitMinutes < 3 && (
              <p className="text-xs text-slate-500 mt-1">Free wait: {Math.max(0, 3 - waitMinutes).toFixed(0)} min left</p>
            )}
          </div>
        )}

        {/* Navigate Button */}
        <Button className="w-full bg-[#1E4DB7]" onClick={() => onAction?.('navigate')}>
          <Navigation className="w-4 h-4 mr-2" /> Navigate to Pickup
        </Button>

        {/* Cancel Button */}
        <Button
          variant="outline"
          className="w-full border-red-200 text-red-600 hover:bg-red-50"
          onClick={() => setShowCancel(true)}
        >
          <X className="w-4 h-4 mr-2" /> Cancel Ride
        </Button>

        <CancelRideDialog
          isOpen={showCancel}
          onClose={() => setShowCancel(false)}
          rideId={ride?.id}
          userType="driver"
          token={token}
          waitMinutes={waitMinutes}
          onCancelled={(data) => onAction?.('cancelled', data)}
        />
      </CardContent>
    </Card>
  );
};

// Rider PIN Display Component
export const RiderPinDisplay = ({ pin }) => {
  if (!pin) return null;

  return (
    <div className="p-4 bg-[#1E4DB7] rounded-xl text-white">
      <p className="text-sm opacity-80 mb-1">Your Ride PIN</p>
      <div className="flex items-center justify-center gap-2">
        {pin.split('').map((digit, i) => (
          <div
            key={i}
            className="w-12 h-14 bg-white/20 rounded-lg flex items-center justify-center text-2xl font-bold"
          >
            {digit}
          </div>
        ))}
      </div>
      <p className="text-xs opacity-70 mt-2 text-center">
        Share this PIN with your driver to start the ride
      </p>
    </div>
  );
};

// Driver On Way Notification
export const DriverOnWayNotification = ({ driverName, eta }) => {
  return (
    <div className="p-4 bg-green-50 rounded-xl border border-green-200 animate-fadeIn">
      <div className="flex items-center gap-3">
        <div className="w-10 h-10 bg-green-500 rounded-full flex items-center justify-center">
          <Navigation className="w-5 h-5 text-white" />
        </div>
        <div>
          <p className="font-semibold text-green-800">{driverName} is on the way!</p>
          <p className="text-sm text-green-600">Please be outside and ready for pickup</p>
          {eta && <p className="text-xs text-green-500 mt-1">ETA: {eta}</p>}
        </div>
      </div>
    </div>
  );
};

export default { CancelRideDialog, DriverQuickActions, RiderPinDisplay, DriverOnWayNotification };
